/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { GetToDosQuery, ToDosArbeitszeiten } from '@/gql/graphql';
import { Konflikt } from '../../components/Table/ToDoTable/entities/Konflikt';
import {
  Abteilung,
  Arbeitsbereich,
} from '../../components/Table/ToDoTable/entities/Arbeitsbereich';
import { Zuschlag } from '../../components/Table/ToDoTable/entities/Zuschlag';
import { Mitarbeiter } from '../../components/Table/ToDoTable/entities/Mitarbeiter';
import { Arbeitszeit } from '../../components/Table/ToDoTable/entities/Arbeitszeit';
import { Zeitstempel } from '../../components/Table/ToDoTable/entities/Zeitstempel';
import dayjs from 'dayjs';
import { Abwesenheit } from '../../components/Table/ToDoTable/entities/Abwesenheit';
import { Rechtshinweis } from '../../components/Table/ToDoTable/entities/Rechtshinweis';
import { mapAbwesenheitsgrund, Pause } from '../../entities';
import {
  Termin,
  TerminWiederholung,
} from '@/domain/arbeitszeiten/entities/Termin';

export function getToDosKonflikte({ data }: { data: GetToDosQuery }) {
  const toDosKonflikte = data.toDos
    .filter((toDo) => toDo.__typename === 'ToDosKonflikte')
    .map((toDoKonflikt) => {
      if (toDoKonflikt.__typename !== 'ToDosKonflikte') {
        return;
      }
      const konfliktArbeitszeit = createArbeitszeit(
        toDoKonflikt.rootArbeitszeit as ToDosArbeitszeiten,
      );
      let newToDoKonflikt;
      if (toDoKonflikt.cause === null) {
        newToDoKonflikt = new Konflikt({
          arbeitszeit: konfliktArbeitszeit,
          conflictCause: null,
        });
      } else if (toDoKonflikt.cause.__typename === 'ToDosAbwesenheiten') {
        newToDoKonflikt = new Konflikt({
          arbeitszeit: konfliktArbeitszeit,
          conflictCause: new Abwesenheit({
            id: toDoKonflikt.cause.id,
            arbeitsbereich: new Arbeitsbereich({
              id: toDoKonflikt.cause.arbeitsbereich.id,
              name: toDoKonflikt.cause.arbeitsbereich.name,
              markierung: toDoKonflikt.cause.arbeitsbereich.markierung,
            }),
            dauer: toDoKonflikt.cause.dauer ?? 0,
            grund: mapAbwesenheitsgrund(toDoKonflikt.cause.grund),
            mitarbeiter: new Mitarbeiter({
              id: toDoKonflikt.cause.mitarbeiter.id,
              vorname: toDoKonflikt.cause.mitarbeiter.vorname,
              nachname: toDoKonflikt.cause.mitarbeiter.nachname,
              arbeitsbereich: new Arbeitsbereich({
                id: toDoKonflikt.rootArbeitszeit.arbeitsbereich.id,
                name: toDoKonflikt.cause.arbeitsbereich.name,
                markierung: toDoKonflikt.cause.arbeitsbereich.markierung,
              }),
            }),
          }),
        });
      } else if (toDoKonflikt.cause.__typename === 'ToDosArbeitszeiten') {
        newToDoKonflikt = new Konflikt({
          arbeitszeit: konfliktArbeitszeit,
          conflictCause: createArbeitszeit(
            toDoKonflikt.cause as ToDosArbeitszeiten,
          ),
        });
      }

      return newToDoKonflikt;
    });
  return toDosKonflikte;
}

function createArbeitszeit(az: ToDosArbeitszeiten): Arbeitszeit {
  const arbeitsbereich = new Arbeitsbereich({
    id: az.arbeitsbereich.id,
    name: az.arbeitsbereich.name,
    markierung: az.arbeitsbereich.markierung,
    abteilungen: az.arbeitsbereich.abteilungen.map((abteilung) => {
      return new Abteilung({
        id: abteilung.id,
      });
    }),
  });

  const zuschlaege = az.zuschlaege.map((zuschlag) => {
    if (zuschlag.betrag.__typename === 'arbeitszeiten_zuschlaege_betrag') {
      return new Zuschlag({
        name: zuschlag.name,
        prozent: zuschlag.prozent,
        betrag: zuschlag.betrag.betrag,
        dauer: zuschlag.dauer,
      });
    }
    return new Zuschlag({
      name: zuschlag.name,
      prozent: zuschlag.prozent,
      dauer: zuschlag.dauer,
    });
  });

  const termin = az.termin
    ? new Termin(
        az.termin.titel,
        TerminWiederholung[az.termin.wiederholung],
        az.termin.bildUrl,
        az.termin.bildName,
      )
    : undefined;

  return new Arbeitszeit({
    mitarbeiter: new Mitarbeiter({
      id: az.mitarbeiter.id,
      vorname: az.mitarbeiter.vorname,
      nachname: az.mitarbeiter.nachname,
      arbeitsbereich: arbeitsbereich,
      imgUrl: az.mitarbeiter.bildUrl,
    }),
    arbeitsbereich: arbeitsbereich,
    id: az.id,
    dauer: az.dauer,
    beginn: az.beginn.zeitpunkt
      ? new Zeitstempel({
          abweichung: az.beginn.abweichung,
          zeitpunkt: dayjs(az.beginn.zeitpunkt),
        })
      : null,
    ende: az.ende.zeitpunkt
      ? new Zeitstempel({
          abweichung: az.ende.abweichung,
          zeitpunkt: dayjs(az.ende.zeitpunkt),
        })
      : null,
    kommen: az.kommen.zeitpunkt
      ? new Zeitstempel({
          abweichung: az.kommen.abweichung,
          zeitpunkt: dayjs(az.kommen.zeitpunkt),
        })
      : null,
    gehen: az.gehen.zeitpunkt
      ? new Zeitstempel({
          abweichung: az.gehen.abweichung,
          zeitpunkt: dayjs(az.gehen.zeitpunkt),
        })
      : null,
    pausen: az.pausen
      ? az.pausen.map(
          (pause) =>
            new Pause({
              id: pause.id,
              bezahlt: pause.bezahlt,
              art: pause.art,
              dauer: pause.dauer,
              beginn: pause.beginn ? new Date(pause.beginn) : null,
              ende: pause.ende ? new Date(pause.ende) : null,
            }),
        )
      : [],
    bemerkung: az.bemerkung,
    zuschlaege: zuschlaege,
    termin,
    rechtshinweise: az.rechtshinweise.map((rh) => {
      return new Rechtshinweis({
        name: rh.name,
        art: rh.art,
        prioritaet: rh.backgroundcolor,
      });
    }),
  });
}
